import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAllBlocks,
  getAllExtraCharges,
  getUnitPricingByBlockId,
  getUnitPricingByProjectId,
  getUnitStatus,
  bulkBookUnits,
  getAllActiveSalesUsersByProjectId
} from '../../services/api/implementation/impl';
import { useParams } from 'react-router-dom';
import { transformData } from '../../../super-admin/utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronDown, CircleCheck, Clock3, Handshake, Home, Info } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import MetricCard from '../../components/ui/MetricCard';
import Loader from '../../../shared/components/ui/Loader';
import Button from '../../../shared/components/ui/Button';
import toast from 'react-hot-toast';

const FACING_OPTIONS = [
  { value: 'north', label: 'North' },
  { value: 'south', label: 'South' },
  { value: 'east', label: 'East' },
  { value: 'west', label: 'West' },
  { value: 'north_east', label: 'North-East' },
  { value: 'north_west', label: 'North-West' },
  { value: 'south_east', label: 'South-East' },
  { value: 'south_west', label: 'South-West' }
];

const FACING_LABELS = FACING_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const formatFacingLabel = (value) => {
  if (!value) return '---';
  return FACING_LABELS[value] || value;
};

const SalesUnitInventoryList = ({
  selectedBlockId: selectedBlockIdFromParent,
  onBlockChange,
  blocksFromParent,
  projectId: projectIdFromParent,
  openBulkUnitUpdate = false,
  bulkUpdatePortalEl = null
}) => {
  const userId = Cookies.get('userId');
  const currentUserId = Number(userId);
  const designation = Cookies.get('userDesignationLevel');
  const userVertical = Cookies.get('userVertical');
  const isManagementVertical = userVertical?.toLowerCase() === 'management';
  const isSalesManager = designation === 'Medium';
  const canBulkUpdateUnits = isSalesManager || isManagementVertical;

  const getUnitManagerId = (unit) =>
    unit?.sales_manager_id ?? unit?.salesManager?.id ?? null;

  const getUnitExecutiveId = (unit) =>
    unit?.sales_executive_id ?? unit?.salesExecutive?.id ?? null;

  const getSalesExecutiveDisplayName = (unit) => {
    const exec = unit?.salesExecutive;
    if (exec?.name) return exec.name;
    if (exec?.email) return exec.email;
    return '---';
  };

  const isUnitAssignedToCurrentUser = (unit) => {
    if (isManagementVertical) return !!unit;
    if (!unit || !Number.isFinite(currentUserId)) return false;
    if (isSalesManager) {
      const managerId = getUnitManagerId(unit);
      return managerId != null && Number(managerId) === currentUserId;
    }
    if (designation === 'Low') {
      const executiveId = getUnitExecutiveId(unit);
      return executiveId != null && Number(executiveId) === currentUserId;
    }
    return false;
  };
  const { id: idFromParams } = useParams();
  const projectId =
    projectIdFromParent != null && projectIdFromParent !== '' && !Number.isNaN(Number(projectIdFromParent))
      ? Number(projectIdFromParent)
      : idFromParams != null && idFromParams !== ''
        ? parseInt(String(idFromParams), 10)
        : null;
  const [newBlockId, setNewBlockId] = useState('');
  const [blocksDynaWithLevels, setBlocksDynaWithLevels] = useState([]);

  const effectiveBlockId = (selectedBlockIdFromParent !== undefined && selectedBlockIdFromParent !== '')
    ? selectedBlockIdFromParent
    : newBlockId;
  const [page, setPage] = useState(1);
  const limit = 50;
  const [expandedRows, setExpandedRows] = useState([]);

  // Dropdown state for header portal dropdown
  const [openDropdown, setOpenDropdown] = useState(null);
  const statusHeaderRef = React.useRef(null);
  const [statusDropdownRect, setStatusDropdownRect] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('Select Status');
  const [selectedType, setSelectedType] = useState('Select Type');
  const [selectedFacing, setSelectedFacing] = useState('Select Facing');
  const [detailModalUnit, setDetailModalUnit] = useState(null);
  const [showExtraChargesBreakdown, setShowExtraChargesBreakdown] = useState(false);
  const [extraChargesModalUnit, setExtraChargesModalUnit] = useState(null);
  const typeHeaderRef = React.useRef(null);
  const [typeDropdownRect, setTypeDropdownRect] = useState(null);
  const facingHeaderRef = React.useRef(null);
  const [facingDropdownRect, setFacingDropdownRect] = useState(null);

  const [selectedUnits, setSelectedUnits] = useState([]);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkSalesExecutiveId, setBulkSalesExecutiveId] = useState('');
  const [isBulkSubmitting, setIsBulkSubmitting] = useState(false);

  // === Queries ===
  const { data: blocksDyna, isLoading: isLoadingBlocks, isFetching: isFetchingBlocks } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const isAllBlocks = effectiveBlockId === 'all';

  const {
    data: unitPricingDataByBlock,
    refetch: refetchUnitPricingDataByBlock,
    isLoading: isLoadingUnitPricingByBlock,
    isFetching: isFetchingUnitPricingByBlock
  } = useQuery({
    queryKey: ['unitPricing', effectiveBlockId, page, limit, selectedStatus],
    queryFn: () => getUnitPricingByBlockId(effectiveBlockId, page, limit, selectedStatus),
    keepPreviousData: true,
    enabled: !!effectiveBlockId && !isAllBlocks
  });

  const {
    data: unitPricingDataByProject,
    refetch: refetchUnitPricingDataByProject,
    isLoading: isLoadingUnitPricingByProject,
    isFetching: isFetchingUnitPricingByProject
  } = useQuery({
    queryKey: ['unitPricingByProject', projectId, page, limit, selectedStatus],
    queryFn: () => getUnitPricingByProjectId(projectId, page, limit, selectedStatus),
    keepPreviousData: true,
    enabled: !!projectId && isAllBlocks
  });

  const isUnitPricingLoading = isAllBlocks
    ? !!projectId && (isLoadingUnitPricingByProject || isFetchingUnitPricingByProject)
    : !!effectiveBlockId && (isLoadingUnitPricingByBlock || isFetchingUnitPricingByBlock);

  const unitListMeta = (isAllBlocks ? unitPricingDataByProject : unitPricingDataByBlock)?.meta ?? {};
  const unitListTotal =
    Number(unitListMeta.total) ||
    (isAllBlocks ? unitPricingDataByProject : unitPricingDataByBlock)?.data?.length ||
    0;
  const unitListHasMore =
    unitListMeta.hasMore === true || page * limit < unitListTotal;

  const isPageLoading =
    !!projectId &&
    ((isLoadingBlocks || isFetchingBlocks) ||
      !effectiveBlockId ||
      isUnitPricingLoading ||
      isBulkSubmitting);

  const unitPricingData = isAllBlocks ? unitPricingDataByProject : unitPricingDataByBlock;

  const { data: unitStatus } = useQuery({
    queryKey: ['unitStatus'],
    queryFn: () => getUnitStatus(),
    enabled: true
  });

  const { data: extraChargesByProject } = useQuery({
    queryKey: ['extraChargesByProject', projectId],
    queryFn: () => getAllExtraCharges(`project_id=${projectId}`),
    enabled: !!projectId
  });

  const { data: salesUsers } = useQuery({
    queryKey: ['salesUsersForBulkUpdate', projectId],
    queryFn: () => getAllActiveSalesUsersByProjectId(projectId),
    enabled: !!projectId && canBulkUpdateUnits
  });

  const { mutate: handleBulkUnitUpdate } = useMutation({
    mutationFn: (payload) => bulkBookUnits(payload),
    onMutate: () => {
      setIsBulkSubmitting(true);
    },
    onSuccess: async () => {
      setIsBulkSubmitting(false);
      toast.success('Unit status and sales executive updated successfully');
      setShowBulkUpdateModal(false);
      setSelectedUnits([]);
      setBulkStatus('');
      setBulkSalesExecutiveId('');
      if (isAllBlocks) {
        await refetchUnitPricingDataByProject();
      } else {
        await refetchUnitPricingDataByBlock();
      }
    },
    onError: (err) => {
      setIsBulkSubmitting(false);
      console.error('Error bulk updating units', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error updating unit status and sales executive'
      );
    }
  });

  // === Effects ===

  useEffect(() => {
    if (openBulkUnitUpdate && canBulkUpdateUnits) {
      setShowBulkUpdateModal(true);
    }
  }, [openBulkUnitUpdate, canBulkUpdateUnits]);

  // When project changes, reset block so data refetches for new project
  useEffect(() => {
    setNewBlockId('');
    setBlocksDynaWithLevels([]);
  }, [projectId]);

  useEffect(() => {
    mixpanel.track('Sales Unit Inventory List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    if (Array.isArray(blocksDyna) && blocksDyna.length > 0) {
      const blocksWithLevels = blocksDyna.filter((block) => block?.levels?.length > 0);
      const sorted = blocksWithLevels.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      sorted.sort((a, b) => a.name.localeCompare(b.name));
      if (sorted.length > 0) {
        setBlocksDynaWithLevels(sorted);
        setNewBlockId((prev) => (prev ? prev : sorted[0].id));
      }
    }
  }, [blocksDyna]);

  useEffect(() => {
    setPage(1);
    setSelectedUnits([]);
  }, [effectiveBlockId, projectId]);

  useEffect(() => {
    if (effectiveBlockId && !isAllBlocks) {
      refetchUnitPricingDataByBlock();
    }
  }, [effectiveBlockId, isAllBlocks, refetchUnitPricingDataByBlock]);

  useEffect(() => {
    setSelectedUnits((prev) =>
      prev.filter((id) => {
        const unit = unitPricingData?.data?.find((u) => u.id === id);
        return isUnitAssignedToCurrentUser(unit);
      })
    );
  }, [unitPricingData?.data, currentUserId, canBulkUpdateUnits, isManagementVertical, designation]);

  // Plot = Independent + no built-up area; House = Independent + built-up area; Apartment = Group
  const getUnitType = (item) => {
    const acc = item?.accommodation;
    const accType = (acc?.accomodationType || acc?.accommodationType || '').toUpperCase();
    const buildUp = Number(acc?.buildUpArea ?? item?.buildUpArea ?? 0);
    if (accType === 'GROUP') return 'Apartment';
    if (accType === 'INDEPENDENT') return buildUp > 0 ? 'House' : 'Plot';
    return buildUp > 0 ? 'House' : 'Plot';
  };

  const getSuperBuildUpArea = (unit) => {
    const acc = unit?.accommodation;
    const fromApi =
      acc?.superBuildUpArea ?? acc?.super_build_up_area ?? acc?.superBuiltUpArea;

    if (fromApi === null || fromApi === undefined || fromApi === '') {
      return null;
    }

    const parsed = parseFloat(fromApi);
    return Number.isNaN(parsed) ? null : parsed;
  };

  // === Data transformations ===
  const UnitPricingFormat = [
    'id',
    'unitName',
    'cost',
    'plc_price',
    'gst',
    'extra_charges',
    'total_price',
    'final_total_price',
    'accommodationName',
    'salesExecutiveName',
    'unitType',
    'facing',
    'status',
    'blockName'
  ];

  const RequiredUnitPricingDataRaw =
    unitPricingData?.data && unitPricingData?.data.length > 0
      ? unitPricingData.data.map((item) =>
            transformData(
              {
                ...item,
                cost: parseInt(item?.cost) || 0,
                plc_price: Math.ceil(item?.plc_price) || 0,
                gst: `₹${(parseInt(item?.gst) || 0).toLocaleString('en-IN')}.00`,
                extra_charges: `₹${(parseInt(item.extra_charges) || 0).toLocaleString('en-IN')}.00`,
                total_price: `₹${(
                  (parseInt(item?.cost) || 0) +
                  (Math.ceil(item?.plc_price) || 0) +
                  (parseInt(item?.gst) || 0)
                ).toLocaleString('en-IN')}.00`,
                final_total_price: `₹${(
                  (parseInt(item?.cost) || 0) +
                  (Math.ceil(item?.plc_price) || 0) +
                  (parseInt(item?.gst) || 0) +
                  (parseInt(item?.extra_charges) || 0)
                ).toLocaleString('en-IN')}.00`,
                accommodationName: item?.accommodation?.name,
                salesExecutiveName: getSalesExecutiveDisplayName(item),
                unitType: getUnitType(item),
                facing: item?.facing || '',
                status: item?.status,
                blockName: item?.block?.name
              },
              UnitPricingFormat
            )
          )
      : [];

  const RequiredUnitPricingData = RequiredUnitPricingDataRaw.filter((row) => {
    if (selectedType !== 'Select Type' && row.unitType !== selectedType) return false;
    if (selectedFacing !== 'Select Facing' && row.facing !== selectedFacing) return false;
    return true;
  });

  const assignableRowsInView = React.useMemo(() => {
    return RequiredUnitPricingData.filter((row) => {
      const unit = unitPricingData?.data?.find((u) => u.id === row.id);
      return isUnitAssignedToCurrentUser(unit);
    });
  }, [RequiredUnitPricingData, unitPricingData?.data, currentUserId, canBulkUpdateUnits, isManagementVertical, designation]);

  const handleUnitSelection = (unitId, isChecked) => {
    const unit = unitPricingData?.data?.find((u) => u.id === unitId);
    if (isChecked && !isUnitAssignedToCurrentUser(unit)) {
      toast.error('You can only update units assigned to you');
      return;
    }
    if (isChecked) {
      setSelectedUnits((prev) => [...prev, unitId]);
    } else {
      setSelectedUnits((prev) => prev.filter((id) => id !== unitId));
    }
  };

  const handleSelectAllUnits = (isChecked) => {
    if (isChecked) {
      if (assignableRowsInView.length === 0) {
        toast.error('No units assigned to you in this view');
        return;
      }
      setSelectedUnits(assignableRowsInView.map((row) => row.id).filter(Boolean));
    } else {
      setSelectedUnits([]);
    }
  };

  const handleBulkUpdateClick = () => {
    if (selectedUnits.length === 0) {
      toast.error('Select at least one unit');
      return;
    }
    setShowBulkUpdateModal(true);
  };

  const handleBulkUpdateSubmit = () => {
    if (!bulkStatus) {
      toast.error('Please select a status');
      return;
    }
    if (!bulkSalesExecutiveId) {
      toast.error('Please select a sales executive');
      return;
    }

    const assignableSelectedIds = selectedUnits.filter((id) => {
      const unit = unitPricingData?.data?.find((u) => u.id === id);
      return isUnitAssignedToCurrentUser(unit);
    });

    if (assignableSelectedIds.length === 0) {
      toast.error('Select at least one unit assigned to you');
      return;
    }

    const payload = {
      ids: assignableSelectedIds,
      status: bulkStatus,
      sales_id: Number(bulkSalesExecutiveId)
    };

    mixpanel.track('Sales Bulk Unit Status Update', {
      user_id: userId,
      project_id: projectId,
      unit_count: selectedUnits.length,
      status: bulkStatus,
      sales_id: payload.sales_id,
      timestamp: new Date().toISOString()
    });

    handleBulkUnitUpdate(payload);
  };

  const allAssignableUnitsSelected =
    assignableRowsInView.length > 0 &&
    assignableRowsInView.every((row) => selectedUnits.includes(row.id));

  const normalizedStatusCounts = React.useMemo(() => {
    const normalizeStatus = (status) =>
      String(status || '')
        .toLowerCase()
        .replace(/[\s-]+/g, '_')
        .replace(/__+/g, '_')
        .trim();

    const empty = {
      booked: 0,
      vacant: 0,
      blocked: 0,
      temporaryBlocked: 0,
      salesDeedComplete: 0
    };

    // Prefer full-scope counts from API meta (not current page length)
    const metaCounts = unitPricingData?.meta?.statusCounts;
    if (metaCounts && typeof metaCounts === 'object') {
      const acc = { ...empty };
      Object.entries(metaCounts).forEach(([status, count]) => {
        const normalized = normalizeStatus(status);
        const n = Number(count) || 0;
        if (normalized === 'booked') acc.booked = n;
        if (normalized === 'vacant') acc.vacant = n;
        if (normalized === 'blocked') acc.blocked = n;
        if (
          normalized === 'temporary_booked' ||
          normalized === 'temporary_blocked' ||
          normalized === 'temporay_blocked'
        ) {
          acc.temporaryBlocked = n;
        }
        if (
          normalized === 'sales_deed_completed' ||
          normalized === 'sales_deed_complete'
        ) {
          acc.salesDeedComplete = n;
        }
      });
      return acc;
    }

    // Fallback: page-only (legacy APIs without statusCounts)
    return RequiredUnitPricingData.reduce((acc, row) => {
      const normalized = normalizeStatus(row?.status);
      if (normalized === 'booked') acc.booked += 1;
      if (normalized === 'vacant') acc.vacant += 1;
      if (normalized === 'blocked') acc.blocked += 1;
      if (
        normalized === 'temporary_booked' ||
        normalized === 'temporary_blocked' ||
        normalized === 'temporay_blocked'
      ) {
        acc.temporaryBlocked += 1;
      }
      if (
        normalized === 'sales_deed_completed' ||
        normalized === 'sales_deed_complete'
      ) {
        acc.salesDeedComplete += 1;
      }
      return acc;
    }, empty);
  }, [RequiredUnitPricingData, unitPricingData?.meta?.statusCounts]);

  const inventoryMetricCards = [
    {
      title: 'Booked',
      value: normalizedStatusCounts.booked,
      icon: CircleCheck,
      iconBgClass: 'bg-[#E9F8F1]',
      iconColorClass: 'text-[#0F766E]',
      valueColorClass: 'text-[#0F766E]',
      borderClass: 'border-[#CFEBDD]'
    },
    {
      title: 'Vacant',
      value: normalizedStatusCounts.vacant,
      icon: Home,
      iconBgClass: 'bg-[#E0F2FE]',
      iconColorClass: 'text-[#0369A1]',
      valueColorClass: 'text-[#0369A1]',
      borderClass: 'border-[#BAE6FD]'
    },
    {
      title: 'Blocked',
      value: normalizedStatusCounts.blocked,
      icon: AlertTriangle,
      iconBgClass: 'bg-[#FFF1F2]',
      iconColorClass: 'text-[#BE123C]',
      valueColorClass: 'text-[#BE123C]',
      borderClass: 'border-[#FFD6DD]'
    },
    {
      title: 'Temporary Blocked',
      value: normalizedStatusCounts.temporaryBlocked,
      icon: Clock3,
      iconBgClass: 'bg-[#FFF7ED]',
      iconColorClass: 'text-[#C2410C]',
      valueColorClass: 'text-[#C2410C]',
      borderClass: 'border-[#FED7AA]'
    },
    {
      title: 'Sales Deed Complete',
      value: normalizedStatusCounts.salesDeedComplete,
      icon: Handshake,
      iconBgClass: 'bg-[#F3E8FF]',
      iconColorClass: 'text-[#7E22CE]',
      valueColorClass: 'text-[#7E22CE]',
      borderClass: 'border-[#E9D5FF]'
    }
  ];

  // === Handlers ===
  const handleToggleExpand = (unitId) => {
    setExpandedRows((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );
  };

  const currentBlockName = blocksFromParent?.length > 0
    ? (blocksFromParent.find((o) => String(o.value) === String(effectiveBlockId))?.label || '---')
    : (blocksDynaWithLevels?.find((b) => String(b.id) === String(effectiveBlockId))?.name || '---');

  const PortalDropdown = ({ anchorRect, children }) => {
    if (!anchorRect) return null;
    const minDropdownWidth = 200;
    const spaceOnRight = typeof window !== 'undefined' ? window.innerWidth - anchorRect.right : 0;
    const openToLeft = spaceOnRight < minDropdownWidth;
    const style = openToLeft
      ? {
          position: 'fixed',
          right: typeof window !== 'undefined' ? window.innerWidth - anchorRect.right : anchorRect.left,
          left: 'auto',
          top: anchorRect.bottom,
          minWidth: Math.max(anchorRect.width, minDropdownWidth),
          maxHeight: '250px',
          overflowY: 'auto'
        }
      : {
          position: 'fixed',
          left: anchorRect.left,
          top: anchorRect.bottom,
          minWidth: anchorRect.width,
          maxHeight: '250px',
          overflowY: 'auto'
        };
    return createPortal(
      <div
        className="absolute bg-white border rounded shadow z-[1000]"
        style={style}
        onClick={(e) => e.stopPropagation()}>
        {children}
      </div>,
      document.body
    );
  };

  const handleHeaderDropdownClick = (type) => {
    const next = openDropdown === type ? null : type;
    setOpenDropdown(next);
    if (next === 'status' && statusHeaderRef?.current) {
      setStatusDropdownRect(statusHeaderRef.current.getBoundingClientRect());
    } else {
      setStatusDropdownRect(null);
    }
    if (next === 'type' && typeHeaderRef?.current) {
      setTypeDropdownRect(typeHeaderRef.current.getBoundingClientRect());
    } else {
      setTypeDropdownRect(null);
    }
    if (next === 'facing' && facingHeaderRef?.current) {
      setFacingDropdownRect(facingHeaderRef.current.getBoundingClientRect());
    } else {
      setFacingDropdownRect(null);
    }
  };

  const handleCloseDropdown = () => {
    setOpenDropdown(null);
    setStatusDropdownRect(null);
    setTypeDropdownRect(null);
    setFacingDropdownRect(null);
  };

  const handleSelectStatusFromHeader = (status) => {
    setPage(1);
    setSelectedStatus(status);
    handleCloseDropdown();
  };

  const handleSelectTypeFromHeader = (type) => {
    setPage(1);
    setSelectedType(type);
    handleCloseDropdown();
  };

  const handleSelectFacingFromHeader = (facing) => {
    setPage(1);
    setSelectedFacing(facing);
    handleCloseDropdown();
  };

  React.useEffect(() => {
    setPage(1);
  }, [effectiveBlockId, projectId]);

  const formatCurrency = (val) => {
    const n = Number(val);
    return isNaN(n) ? '—' : `₹${n.toLocaleString('en-IN')}`;
  };

  // Extra charges breakdown for current unit: filter by accommodation id
  const extraChargesBreakdown = React.useMemo(() => {
    const list = extraChargesByProject || [];
    const accommodationId = detailModalUnit?.accommodation?.id;
    if (!accommodationId || !Array.isArray(list)) return [];
    return list
      .map((charge) => {
        const accomodations = charge?.accomodations || [];
        const matched = accomodations.find(
          (a) => Number(a?.accomodation_id) === Number(accommodationId)
        );
        if (!matched) return null;
        const basePrice = Number(charge?.price) || 0;
        const gstPct = Number(charge?.gst) || 0;
        const gstAmount = (basePrice * gstPct) / 100;
        const totalPrice = Number(matched?.total_price) ?? (basePrice + gstAmount);
        const isMandatory = charge?.isMandatory === true || charge?.isMandatory === 'true';
        return {
          name: charge?.name || '—',
          price: basePrice,
          gst: gstPct,
          gstAmount,
          total_price: totalPrice,
          optional: !isMandatory
        };
      })
      .filter(Boolean);
  }, [extraChargesByProject, detailModalUnit?.accommodation?.id]);

  // Extra charges breakdown for the row-level modal (when user clicks info icon in table)
  const extraChargesBreakdownForModal = React.useMemo(() => {
    const list = extraChargesByProject || [];
    const accommodationId = extraChargesModalUnit?.accommodation?.id;
    if (!accommodationId || !Array.isArray(list)) return [];
    return list
      .map((charge) => {
        const accomodations = charge?.accomodations || [];
        const matched = accomodations.find(
          (a) => Number(a?.accomodation_id) === Number(accommodationId)
        );
        if (!matched) return null;
        const basePrice = Number(charge?.price) || 0;
        const gstPct = Number(charge?.gst) || 0;
        const gstAmount = (basePrice * gstPct) / 100;
        const totalPrice = Number(matched?.total_price) ?? (basePrice + gstAmount);
        const isMandatory = charge?.isMandatory === true || charge?.isMandatory === 'true';
        return {
          name: charge?.name || '—',
          price: basePrice,
          gst: gstPct,
          gstAmount,
          total_price: totalPrice,
          optional: !isMandatory
        };
      })
      .filter(Boolean);
  }, [extraChargesByProject, extraChargesModalUnit?.accommodation?.id]);

  return (
    <>
      <Loader isVisible={isPageLoading} />

      {showBulkUpdateModal && canBulkUpdateUnits && (
        <Modal
          isDirty
          confirmOnClose
          width={40}
          name="Update status & sales executive"
          onClose={() => {
            setShowBulkUpdateModal(false);
            setBulkStatus('');
            setBulkSalesExecutiveId('');
          }}>
          <div className="p-6">
            <p className="mb-4 text-sm text-gray-600">
              Update {selectedUnits.length} selected unit{selectedUnits.length === 1 ? '' : 's'} with a
              new status and sales executive.
            </p>

            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={bulkStatus}
                onChange={(e) => setBulkStatus(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select status</option>
                {unitStatus?.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Sales executive <span className="text-red-500">*</span>
              </label>
              <select
                value={bulkSalesExecutiveId}
                onChange={(e) => setBulkSalesExecutiveId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Select sales executive</option>
                {salesUsers?.data?.map((sales) => (
                  <option key={sales.id} value={sales.id}>
                    {sales.name || sales.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                displayType="secondary"
                onClick={() => {
                  setShowBulkUpdateModal(false);
                  setBulkStatus('');
                  setBulkSalesExecutiveId('');
                }}
                disabled={isBulkSubmitting}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleBulkUpdateSubmit}
                disabled={isBulkSubmitting || !bulkStatus || !bulkSalesExecutiveId}>
                {isBulkSubmitting ? 'Updating...' : 'Update units'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {detailModalUnit && (
        <Modal
          width={50}
          name="Unit Details"
          onClose={() => {
            setDetailModalUnit(null);
            setShowExtraChargesBreakdown(false);
          }}>
          <div className="space-y-5">
            {/* Basic info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Unit Name</p>
                <p className="text-gray-900 font-medium">{detailModalUnit.unitName || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</p>
                <p className="text-gray-900 font-medium">{detailModalUnit.status || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Type</p>
                <p className="text-gray-900 font-medium">{getUnitType(detailModalUnit) || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Facing</p>
                <p className="text-gray-900 font-medium">
                  {formatFacingLabel(detailModalUnit.facing)}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Block</p>
                <p className="text-gray-900">{detailModalUnit.block?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Level / Floor</p>
                <p className="text-gray-900">{detailModalUnit.level?.name ?? '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Project</p>
                <p className="text-gray-900">{detailModalUnit.project?.name || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Accommodation</p>
                <p className="text-gray-900">{detailModalUnit.accommodation?.name || '—'}</p>
              </div>
              {/* Area by type: Plot = plot area only; House = plot + buildup + carpet; Apartment (Group) = buildup + carpet only */}
              {(() => {
                const unitType = getUnitType(detailModalUnit);
                const acc = detailModalUnit.accommodation;
                const plotArea = Number(acc?.plotArea ?? detailModalUnit.area ?? 0);
                const buildUpArea = Number(acc?.buildUpArea ?? detailModalUnit.buildUpArea ?? 0);
                const carpetArea = Number(acc?.carpetArea ?? detailModalUnit.carpetArea ?? 0);
                const superBuildUpArea = getSuperBuildUpArea(detailModalUnit);
                const unit = acc?.measurementUnit || detailModalUnit.project?.measurement_type || 'sq/ft';
                if (unitType === 'Plot' && plotArea > 0) {
                  return (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plot area</p>
                      <p className="text-gray-900">{plotArea.toLocaleString('en-IN')} {unit}</p>
                    </div>
                  );
                }
                if (unitType === 'House') {
                  return (
                    <>
                      {plotArea > 0 && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Plot area</p>
                          <p className="text-gray-900">{plotArea.toLocaleString('en-IN')} {unit}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Build-up area</p>
                        <p className="text-gray-900">{(buildUpArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Carpet area</p>
                        <p className="text-gray-900">{(carpetArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                      {superBuildUpArea != null && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Super build-up area</p>
                          <p className="text-gray-900">{superBuildUpArea.toLocaleString('en-IN')} {unit}</p>
                        </div>
                      )}
                    </>
                  );
                }
                if (unitType === 'Apartment') {
                  return (
                    <>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Build-up area</p>
                        <p className="text-gray-900">{(buildUpArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Carpet area</p>
                        <p className="text-gray-900">{(carpetArea || 0).toLocaleString('en-IN')} {unit}</p>
                      </div>
                      {superBuildUpArea != null && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Super build-up area</p>
                          <p className="text-gray-900">{superBuildUpArea.toLocaleString('en-IN')} {unit}</p>
                        </div>
                      )}
                    </>
                  );
                }
                return null;
              })()}
            </div>

            {/* Pricing */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold text-gray-700 mb-3">Pricing</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-gray-50 rounded-lg p-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cost</span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.cost)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">PLC Charges</span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.plc_price)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST</span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.gst)}</span>
                </div>
                <div className="flex justify-between col-span-full border-t pt-2 mt-1">
                  <span className="text-gray-800 font-medium">Total price</span>
                  <span className="font-semibold">
                    {formatCurrency(
                      (Number(detailModalUnit.cost) || 0) +
                        (Number(detailModalUnit.plc_price) || 0) +
                        (Number(detailModalUnit.gst) || 0)
                    )}
                  </span>
                </div>
                <div className="flex justify-between col-span-full items-center">
                  <span className="text-gray-600 flex items-center gap-1.5">
                    Extra charges
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowExtraChargesBreakdown((prev) => !prev);
                      }}
                      className={`inline-flex items-center justify-center w-5 h-5 rounded-full transition-colors ${
                        showExtraChargesBreakdown ? 'bg-blue-200 text-blue-800' : 'bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-800'
                      }`}
                      aria-label="Extra charges breakdown">
                      <Info size={14} />
                    </button>
                  </span>
                  <span className="font-medium">{formatCurrency(detailModalUnit.extra_charges)}</span>
                </div>
                {showExtraChargesBreakdown && (
                  <div className="col-span-full pl-2 pt-1 pb-1 border-l-2 border-gray-200 ml-1 space-y-1">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Breakdown</p>
                    {extraChargesBreakdown.length === 0 ? (
                      <p className="text-sm text-gray-500">No extra charges for this accommodation.</p>
                    ) : (
                      <>
                        <div className="grid grid-cols-5 gap-2 text-xs font-medium text-gray-500 mb-1">
                          <span>Name</span>
                          <span>Optional / Required</span>
                          <span className="text-right">Price</span>
                          <span className="text-right">GST (% / ₹)</span>
                          <span className="text-right">Total</span>
                        </div>
                        {extraChargesBreakdown.map((row, idx) => (
                          <div key={idx} className="grid grid-cols-5 gap-2 text-sm">
                            <span className="text-gray-700">{row.name}</span>
                            <span>
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                  row.optional ? 'bg-gray-200 text-gray-700' : 'bg-amber-100 text-amber-800'
                                }`}>
                                {row.optional ? 'Optional' : 'Required'}
                              </span>
                            </span>
                            <span className="text-right">{formatCurrency(row.price)}</span>
                            <span className="text-right">
                              {row.gst != null && row.gst !== 0 ? (
                                <>{Number(row.gst).toFixed(2)}% ({formatCurrency(row.gstAmount)})</>
                              ) : (
                                '—'
                              )}
                            </span>
                            <span className="text-right font-medium">{formatCurrency(row.total_price)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-sm border-t border-gray-200 pt-1 mt-1 font-medium">
                          <span>Total</span>
                          <span>{formatCurrency(extraChargesBreakdown.reduce((s, r) => s + r.total_price, 0))}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <div className="flex justify-between col-span-full border-t pt-2 mt-1">
                  <span className="text-gray-800 font-medium">Final price</span>
                  <span className="font-semibold">
                    {formatCurrency(
                      (Number(detailModalUnit.cost) || 0) +
                        (Number(detailModalUnit.plc_price) || 0) +
                        (Number(detailModalUnit.gst) || 0) +
                        (Number(detailModalUnit.extra_charges) || 0)
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* Closed deal */}
            {detailModalUnit.closedDeal?.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-green-700 mb-2">Closed deal</p>
                <div className="bg-green-50 rounded-lg p-4 space-y-2 text-sm">
                  {detailModalUnit.closedDeal.map((cd) => (
                    <div key={cd.id} className="flex justify-between flex-wrap gap-2">
                      <span>Total price: {formatCurrency(cd.total_price)}</span>
                      <span>Due: {formatCurrency(cd.due_price)}</span>
                      <span>Status: {cd.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Under consideration */}
            {detailModalUnit.underConsideration?.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm font-semibold text-blue-700 mb-2">Under consideration</p>
                <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm">
                  {detailModalUnit.underConsideration.map((uc) => (
                    <div key={uc.id} className="flex justify-between flex-wrap gap-2">
                      <span>Proposed: {formatCurrency(uc.unit_price || uc.customer_proposal)}</span>
                      <span>{uc.approved ? 'Approved' : 'Pending'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Extra Charges modal (from table row info icon) */}
      {extraChargesModalUnit && (
        <Modal
          width={45}
          name="Extra Charges"
          onClose={() => setExtraChargesModalUnit(null)}>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Unit: <span className="font-medium text-gray-800">{extraChargesModalUnit.unitName || '—'}</span>
              {extraChargesModalUnit.accommodation?.name && (
                <> · {extraChargesModalUnit.accommodation.name}</>
              )}
            </p>
            <p className="text-sm text-gray-600">
              Total extra charges: {formatCurrency(extraChargesModalUnit.extra_charges)}
            </p>
            {extraChargesBreakdownForModal.length === 0 ? (
              <p className="text-sm text-gray-500">No extra charges for this accommodation.</p>
            ) : (
              <>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Name</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-700">Optional / Required</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">Price</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">GST (% / ₹)</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {extraChargesBreakdownForModal.map((row, idx) => (
                        <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-800">{row.name}</td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                row.optional ? 'bg-gray-200 text-gray-700' : 'bg-amber-100 text-amber-800'
                              }`}>
                              {row.optional ? 'Optional' : 'Required'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.price)}</td>
                          <td className="px-3 py-2 text-right">
                            {row.gst != null && row.gst !== 0 ? (
                              <>{Number(row.gst).toFixed(2)}% ({formatCurrency(row.gstAmount)})</>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-medium">{formatCurrency(row.total_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-200 pt-2 font-medium">
                  <span>Total</span>
                  <span>{formatCurrency(extraChargesBreakdownForModal.reduce((s, r) => s + r.total_price, 0))}</span>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      <div className="px-4 mt-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          {inventoryMetricCards.map((card) => {
            const Icon = card.icon;
            return (
              <MetricCard
                key={card.title}
                title={card.title}
                value={card.value}
                icon={<Icon size={18} />}
                iconBgClass={card.iconBgClass}
                iconColorClass={card.iconColorClass}
                valueColorClass={card.valueColorClass}
                borderClass={card.borderClass}
                valueClassName="text-xl sm:text-2xl"
                titleClassName="text-[11px]"
              />
            );
          })}
        </div>
      </div>

      {canBulkUpdateUnits &&
        bulkUpdatePortalEl &&
        createPortal(
          <Button
            type="button"
            onClick={handleBulkUpdateClick}
            disabled={selectedUnits.length === 0}
            customStyle={{
              padding: '0.75rem 1.5rem',
              fontSize: '14px',
              whiteSpace: 'nowrap',
              opacity: selectedUnits.length === 0 ? 0.5 : 1,
              cursor: selectedUnits.length === 0 ? 'not-allowed' : 'pointer'
            }}>
            Update status & executive ({selectedUnits.length} selected)
          </Button>,
          bulkUpdatePortalEl
        )}

     <div className="table-container mt-4 flex flex-col overflow-hidden h-[calc(100vh-18rem)] px-4">
        {/* Single table with sticky thead so header and body columns stay aligned */}
        <div className="flex-1 min-h-0 flex flex-col rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto">
            <table className="text-left text-sm w-full table-fixed" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                {canBulkUpdateUnits && <col className="w-[3%]" />}
                <col className="w-[7%]" />
                <col className="w-[7%]" />
                <col className="w-[7%]" />
                <col className="w-[8%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[10%]" />
                <col className="w-[11%]" />
                <col className="w-[7%]" />
                <col className="w-[8%]" />
                {canBulkUpdateUnits && <col className="w-[9%]" />}
                <col className={canBulkUpdateUnits ? 'w-[9%]' : 'w-[11%]'} />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-gray-100 text-gray-600">
                <tr className="h-12 w-full">
                  {canBulkUpdateUnits && (
                    <th className="px-2 py-3 border-b border-gray-200 font-medium rounded-tl-xl">
                      <input
                        type="checkbox"
                        checked={allAssignableUnitsSelected}
                        disabled={assignableRowsInView.length === 0}
                        onChange={(e) => handleSelectAllUnits(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label="Select all units assigned to you"
                      />
                    </th>
                  )}
                  <th
                    className={`px-4 py-3 border-b border-gray-200 font-medium ${
                      !canBulkUpdateUnits ? 'rounded-tl-xl' : ''
                    }`}>
                    Block Name
                  </th>
                  <th className="px-4 py-3 border-b border-gray-200 font-medium">Unit Name</th>
                <th className="px-4 py-3 border-b border-gray-200 font-medium relative" ref={typeHeaderRef}>
                  <span>Type{selectedType !== 'Select Type' ? ` (${selectedType})` : ''}</span>
                  <button
                    type="button"
                    className="ml-1 align-middle inline-flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('type');
                    }}
                    aria-label="Filter by type">
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'type' && typeDropdownRect && (
                    <PortalDropdown anchorRect={typeDropdownRect}>
                      {['Select Type', 'Plot', 'House', 'Apartment'].map((t) => (
                        <div
                          key={t}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            selectedType === t ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectTypeFromHeader(t)}>
                          {t}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th
                  className="px-4 py-3 border-b border-gray-200 font-medium relative min-w-[100px] whitespace-nowrap"
                  ref={facingHeaderRef}>
                  <span>
                    Facing
                    {selectedFacing !== 'Select Facing'
                      ? ` (${formatFacingLabel(selectedFacing)})`
                      : ''}
                  </span>
                  <button
                    type="button"
                    className="ml-1 align-middle inline-flex items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('facing');
                    }}
                    aria-label="Filter by facing">
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'facing' && facingDropdownRect && (
                    <PortalDropdown anchorRect={facingDropdownRect}>
                      <div
                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                          selectedFacing === 'Select Facing' ? 'bg-blue-100' : ''
                        }`}
                        onClick={() => handleSelectFacingFromHeader('Select Facing')}>
                        Select Facing
                      </div>
                      {FACING_OPTIONS.map((opt) => (
                        <div
                          key={opt.value}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            selectedFacing === opt.value ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectFacingFromHeader(opt.value)}>
                          {opt.label}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
                <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[100px] whitespace-nowrap">Unit Price</th>
                <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[110px] whitespace-nowrap">PLC Charges</th>
                {/* <th className="px-4 py-3 border-b border-gray-300 font-normal min-w-[80px] whitespace-nowrap">GST</th> */}
                {/* <th className="px-4 py-3 border-b border-gray-300 font-normal min-w-[100px] whitespace-nowrap">Total Price</th> */}
                <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[100px] whitespace-nowrap">
                  Extra Charges
                </th>
                <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[110px] whitespace-nowrap">Final Total Price</th>
                <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[80px] whitespace-nowrap">Discount</th>
                <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[100px] whitespace-nowrap">Acc Name</th>
                {canBulkUpdateUnits && (
                  <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[100px] whitespace-nowrap">
                    Sales Executive
                  </th>
                )}
                <th className="px-4 py-3 border-b border-gray-200 font-medium min-w-[100px] relative whitespace-nowrap bg-gray-100 rounded-tr-xl" ref={statusHeaderRef}>
                  <span>Status</span>
                  <button
                    className="ml-1 align-middle"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleHeaderDropdownClick('status');
                    }}>
                    <ChevronDown size={16} />
                  </button>
                  {openDropdown === 'status' && statusDropdownRect && (
                    <PortalDropdown anchorRect={statusDropdownRect}>
                      <div
                        className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                          selectedStatus === 'Select Status' ? 'bg-blue-100' : ''
                        }`}
                        onClick={() => handleSelectStatusFromHeader('Select Status')}>
                        Select Status
                      </div>
                      {unitStatus?.map((s) => (
                        <div
                          key={s}
                          className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                            selectedStatus === s ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => handleSelectStatusFromHeader(s)}>
                          {s}
                        </div>
                      ))}
                    </PortalDropdown>
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              {RequiredUnitPricingData && RequiredUnitPricingData.length > 0 ? (
                RequiredUnitPricingData.map((row, idx) => {
                  const original = unitPricingData?.data?.find((u) => u.id === row.id);
                  const hasExpandable =
                    original?.underConsideration?.length > 0 ||
                    original?.closedDeal?.length > 0 ||
                    original?.pricingHistory?.length > 1;
                  return (
                    <React.Fragment key={row.id || idx}>
                      <tr
                        className="border-b border-gray-100 hover:bg-gray-50 bg-white h-[3.35rem] cursor-pointer"
                        onClick={() => setDetailModalUnit(original)}>
                        {canBulkUpdateUnits && (
                          <td
                            className="px-2 py-3 align-middle"
                            onClick={(e) => e.stopPropagation()}>
                            {isUnitAssignedToCurrentUser(original) ? (
                              <input
                                type="checkbox"
                                checked={selectedUnits.includes(row.id)}
                                onChange={(e) => handleUnitSelection(row.id, e.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600"
                              />
                            ) : null}
                          </td>
                        )}
                        <td className="px-4 py-3 font-semibold text-gray-900" onClick={(e) => e.stopPropagation()}>
                          {row.blockName ?? currentBlockName}
                        </td>
                        <td className="px-4 py-3">
                          {hasExpandable && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleExpand(row.id);
                              }}
                              style={{ marginRight: 8 }}>
                              {expandedRows.includes(row.id) ? '-' : '+'}
                            </button>
                          )}
                          {row.unitName || '---'}
                        </td>
                        <td className="px-4 py-3">{row.unitType || '---'}</td>
                        <td className="px-4 py-3">{formatFacingLabel(row.facing)}</td>
                        <td className="px-4 py-3">{row.cost || '---'}</td>
                        <td className="px-4 py-3">{row.plc_price || '---'}</td>
                        {/* <td className="px-4 py-3">{row.gst || '---'}</td> */}
                        {/* <td className="px-4 py-3">{row.total_price || '---'}</td> */}
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1">
                            {row.extra_charges || '---'}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExtraChargesModalUnit(original);
                              }}
                              className="p-0.5 rounded hover:bg-gray-200 text-gray-500 hover:text-blue-600 transition-colors"
                              title="View extra charges breakdown"
                              aria-label="View extra charges breakdown">
                              <Info size={16} />
                            </button>
                          </span>
                        </td>
                        <td className="px-4 py-3">{row.final_total_price || '---'}</td>
                        <td className="px-4 py-3">---</td>
                        <td className="px-4 py-3">{row.accommodationName || '---'}</td>
                        {canBulkUpdateUnits && (
                          <td className="px-4 py-3 truncate" title={row.salesExecutiveName || '---'}>
                            {row.salesExecutiveName || '---'}
                          </td>
                        )}
                        <td className="px-4 py-3">{row.status || '---'}</td>
                      </tr>
                      {/* Pricing History Sub-rows */}
                      {expandedRows.includes(row.id) &&
                        original?.pricingHistory?.length > 1 &&
                        original.pricingHistory.slice(1)?.map((ph, i) => {
                          const cost = Number(ph.cost) || 0;
                          const plcPrice = Number(ph.plc_price) || 0;
                          const gst = Number(ph.gst) || 0;
                          const totalPrice = cost + plcPrice + gst;
                          return (
                            <tr key={ph.id || `ph-${i}`} className="bg-amber-50 mb-2">
                              {canBulkUpdateUnits && <td className="px-2 py-2" />}
                              <td className="px-4 py-2 font-semibold text-amber-800">
                                Old Price v{i + 1}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">
                                {cost > 0 ? `₹${cost.toLocaleString('en-IN')}` : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {plcPrice > 0 ? `₹${plcPrice.toLocaleString('en-IN')}` : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {gst > 0 ? `₹${gst.toLocaleString('en-IN')}` : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {totalPrice > 0
                                  ? `₹${totalPrice.toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">
                                {ph.extra_charges
                                  ? `₹${Number(ph.extra_charges).toLocaleString('en-IN')}`
                                  : '---'}
                              </td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              <td className="px-4 py-2">---</td>
                              {canBulkUpdateUnits && <td className="px-4 py-2">---</td>}
                              <td className="px-4 py-2">---</td>
                            </tr>
                          );
                        })}
                      {/* Under Consideration Sub-row */}
                      {expandedRows.includes(row.id) &&
                        original?.underConsideration?.length > 0 &&
                        original.underConsideration.map((uc, i) => (
                          <tr key={uc.id || i} className="bg-blue-50 mb-2">
                            {canBulkUpdateUnits && <td className="px-2 py-2" />}
                            <td className="px-4 py-2 font-semibold text-blue-800">
                              Under Consideration
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">
                              {uc.unit_price || uc.customer_proposal
                                ? `₹${Number(uc.unit_price || uc.customer_proposal).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.plc_price
                                ? `₹${Number(uc.plc_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.gst_price || uc.gst
                                ? `₹${Number(uc.gst_price || uc.gst).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.total_price
                                ? `₹${Number(uc.total_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {uc.extra_charges
                                ? `₹${Number(uc.extra_charges).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">
                              {uc.discount
                                ? `₹${Number(uc.discount).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">---</td>
                            {canBulkUpdateUnits && <td className="px-4 py-2">---</td>}
                            <td className="px-4 py-2">
                              {uc.approved ? 'Approved' : 'Approval' || '---'}
                            </td>
                          </tr>
                        ))}
                      {/* Deal Closed Sub-row */}
                      {expandedRows.includes(row.id) &&
                        original?.closedDeal?.length > 0 &&
                        original.closedDeal?.map((cd, i) => (
                          <tr key={cd.id || i} className="bg-green-50 mb-2">
                            {canBulkUpdateUnits && <td className="px-2 py-2" />}
                            <td className="px-4 py-2 font-semibold text-green-800">
                              Deal Closed
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">
                              {cd.unit_price
                                ? `₹${Number(cd.unit_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.plc_price
                                ? `₹${Number(cd.plc_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.gst_price
                                ? `₹${Number(cd.gst_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.total_price
                                ? `₹${Number(cd.total_price).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">
                              {cd.extra_charges
                                ? `₹${Number(cd.extra_charges).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">---</td>
                            <td className="px-4 py-2">
                              {cd.discount
                                ? `₹${Number(cd.discount).toLocaleString('en-IN')}`
                                : '---'}
                            </td>
                            <td className="px-4 py-2">---</td>
                            {canBulkUpdateUnits && <td className="px-4 py-2">---</td>}
                            <td className="px-4 py-2">{cd.status || 'N/A'}</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={canBulkUpdateUnits ? 12 : 10} className="text-center py-10 align-middle">
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <h2 className="text-lg font-bold text-gray-800 mb-1">No Data Found</h2>
                      <p className="text-gray-600 text-sm">
                        The page you are trying to access has no data.
                        <br />
                        Proceed with adding data.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-white">
            <span className="text-xs text-gray-600">
              Showing {unitPricingData?.data?.length || 0} of {unitListTotal}
              {unitListHasMore ? ' · more available' : ''}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || isUnitPricingLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40">
                Prev
              </button>
              <span className="text-xs text-gray-500">Page {page}</span>
              <button
                type="button"
                disabled={!unitListHasMore || isUnitPricingLoading}
                onClick={() => setPage((p) => p + 1)}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40">
                Next
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SalesUnitInventoryList;
